import { faMinus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useHaveValuesChanged from '../../../../hooks/useHaveValuesChanged';
import useSearchQueryDateParam from '../../../../hooks/useSearchQueryDateParam';
import useSearchQueryParam from '../../../../hooks/useSearchQueryParam';
import { debounceTimeout } from '../../../../utils/constants/misc';
import { httpDateFormat } from '../../../../utils/helpers/formatting';
import { renderIcon } from '../../../../utils/helpers/icon';
import {
  getSearchQueryParam,
  tryDateSearchParam,
} from '../../../../utils/helpers/searchQuery';
import { TableProps } from '../../../DataTable/Table/Table';
import FiltersCounter from '../../Components/Filters/FiltersCounter';

function useTableFilters(
  page: number,
  setPage: TableProps['setPage'],
  limit: number
) {
  const { t } = useTranslation();
  const location = useLocation();
  const [dateFrom, setDateFrom] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'date_from') ?? ''
      ) ?? dayjs().toDate()
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'date_to') ?? ''
      ) ?? dayjs().toDate()
  );
  const [clientFromName, setClientFromName] = useState<string>(
    () => getSearchQueryParam(location.search, 'clientFromName') ?? ''
  );
  const [clientToName, setClientToName] = useState<string>(
    () => getSearchQueryParam(location.search, 'clientToName') ?? ''
  );
  const [serialNumber, setSerialNumber] = useState<string>(
    () => getSearchQueryParam(location.search, 'serialNumber') ?? ''
  );
  const [reference1, setReference1] = useState<string>(
    () => getSearchQueryParam(location.search, 'reference1') ?? ''
  );

  useEffect(() => {
    if (!setPage) {
      return;
    }
    setPage(1);
  }, [dateFrom, dateTo, setPage]);

  const debounceSerialNumber = useDebounce(serialNumber, debounceTimeout);
  const debouncedClientFromName = useDebounce(clientFromName, debounceTimeout);
  const debouncedClientToName = useDebounce(clientToName, debounceTimeout);
  const debouncedReference1 = useDebounce(reference1, debounceTimeout);

  const filtersArr = useMemo(
    () => [
      dateFrom,
      dateTo,
      debounceSerialNumber,
      debouncedClientFromName,
      debouncedClientToName,
      debouncedReference1,
    ],
    [
      dateFrom,
      dateTo,
      debounceSerialNumber,
      debouncedClientFromName,
      debouncedClientToName,
      debouncedReference1,
    ]
  );

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  const httpFiltersObj = useMemo(() => {
    const _dateFrom = dayjs(dateFrom as any);
    const _dateTo = dayjs(dateTo as any);

    return {
      date_from: httpDateFormat(_dateFrom),
      date_to: httpDateFormat(_dateTo),
      seriskiBroj: debounceSerialNumber,
      klientOdIme: debouncedClientFromName,
      klientDoIme: debouncedClientToName,
      reference1: debouncedReference1,
      page: haveFiltersChanged ? 1 : page,
      limit,
    };
  }, [
    dateFrom,
    dateTo,
    debounceSerialNumber,
    debouncedClientFromName,
    debouncedClientToName,
    debouncedReference1,
    haveFiltersChanged,
    page,
    limit,
  ]);

  useSearchQueryDateParam('date_from', dateFrom);
  useSearchQueryDateParam('date_to', dateTo);
  useSearchQueryParam('serialNumber', debounceSerialNumber);
  useSearchQueryParam('clientFromName', debouncedClientFromName);
  useSearchQueryParam('clientToName', debouncedClientToName);
  useSearchQueryParam('reference1', debouncedReference1);

  const basicFiltersActiveFilterCount = Object.values({
    dateFrom, // date_from and date_to is count like one
    debounceSerialNumber,
    debouncedClientFromName,
    debouncedClientToName,
    debouncedReference1,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_date_from">{t('Created Date')}</label>
            <div className="sidebar_filter_row">
              <Calendar
                id="filter_date_from"
                value={dateFrom ?? undefined}
                monthNavigator
                yearNavigator
                dateFormat="dd/mm/yy"
                yearRange={`2009:${dayjs().format('YYYY')}`}
                maxDate={new Date()}
                onChange={(e) => setDateFrom(e.value as Date | null)}
                inputClassName={classNames({
                  sidebar_filter_active: dateFrom,
                })}
              />
              <span style={{ margin: 'auto' }}>{renderIcon(faMinus)}</span>
              <Calendar
                id="filter_date_to"
                value={dateTo ?? undefined}
                onChange={(e) => {
                  setDateTo(e.value as Date | null);
                }}
                dateFormat="dd/mm/yy"
                maxDate={new Date()}
                monthNavigator
                yearNavigator
                yearRange={`2009:${dayjs().format('YYYY')}`}
                inputClassName={classNames({
                  sidebar_filter_active: dateTo,
                })}
              />
            </div>
          </div>
          <div className="sidebar_filter">
            <label htmlFor="serial_number_filter">{t('Serial Number')}</label>
            <InputText
              id="client_to_name_filter"
              value={serialNumber ?? ''}
              onChange={(e) => setSerialNumber(e.target.value)}
              className={classNames({
                sidebar_filter_active: serialNumber,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="client_from_name_filter">{t('Sender')}</label>
            <InputText
              id="client_from_name_filter"
              value={clientFromName ?? ''}
              onChange={(e) => setClientFromName(e.target.value)}
              className={classNames({
                sidebar_filter_active: clientFromName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="client_to_name_filter">{t('Recipient')}</label>
            <InputText
              id="client_to_name_filter"
              value={clientToName ?? ''}
              onChange={(e) => setClientToName(e.target.value)}
              className={classNames({
                sidebar_filter_active: clientToName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="reference1_filter">{t('Reference1')}</label>
            <InputText
              id="reference1_filter"
              value={reference1 ?? ''}
              onChange={(e) => setReference1(e.target.value)}
              className={classNames({
                sidebar_filter_active: reference1,
              })}
            />
          </div>
        </AccordionTab>
      </Accordion>
    ),
    [
      dateFrom,
      dateTo,
      serialNumber,
      clientFromName,
      clientToName,
      reference1,
      t,
      basicFiltersActiveFilterCount,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setDateFrom(new Date());
    setDateTo(new Date());
    setSerialNumber('');
    setClientFromName('');
    setClientToName('');
    setReference1('');
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
